import { Alert } from "@/components/ui/Alert";
import { AgentProviderIcon } from "@/components/agent-providers/AgentProviderIcon";
import { Label } from "@/components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { Skeleton } from "@/components/ui/Skeleton";
import { Text } from "@/components/ui/Text";
import { useOrganization, useProject } from "@/contexts/Auth";
import { useSdkClient } from "@/contexts/Sdk";
import { getServerURL } from "@/lib/utils";
import { useRoutes } from "@/routes";
import type { Plugin } from "@gram/client/models/components/plugin.js";
import { useListMcpServersForOrg } from "@gram/client/react-query/listMcpServersForOrg.js";
import { usePlugins } from "@gram/client/react-query/plugins.js";
import { useQueries } from "@tanstack/react-query";
import { useState, type JSX, type ReactNode } from "react";
import { Link } from "react-router";
import { CodeBlock } from "./CodeBlock";
import { PanelTab, PanelTabs } from "./PanelTabs";
import {
  ACTION_DOCS,
  ACTION_TAG,
  WORKFLOW_AGENTS,
  WORKFLOW_AGENT_OPTIONS,
  pluginGateways,
  pluginServersWithoutAuthorization,
  pluginsForEveryAgent,
  unreachablePluginServers,
  workflowYaml,
  type JobScope,
  type WorkflowAgent,
} from "./githubActions";
import { Links, SetupError, SetupWarning } from "./SetupFields";
import type { SetupServer } from "./setupInstructions";

/** How long a plugin read for this step is reused before it is read again. */
const PLUGIN_STALE_TIME_MS = 30_000;

export function PluginsStep({
  agentName,
  servers,
}: {
  agentName: string;
  servers: SetupServer[];
}): JSX.Element {
  const project = useProject();
  const sdk = useSdkClient();
  const plugins = usePlugins({ gramProject: project.slug }, undefined, {
    enabled: project.slug !== "",
    throwOnError: false,
  });
  // The list carries a count of each plugin's assignments, not the
  // assignments, so each assigned plugin is read for its audiences.
  const assigned = (plugins.data?.plugins ?? []).filter(
    (plugin) => (plugin.assignmentCount ?? 0) > 0,
  );
  const details = useQueries({
    queries: assigned.map((plugin) => ({
      queryKey: ["github-actions-setup-plugin", project.slug, plugin.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        sdk.plugins.getPlugin(
          { id: plugin.id, gramProject: project.slug },
          undefined,
          { signal },
        ),
      retry: false,
      throwOnError: false,
      staleTime: PLUGIN_STALE_TIME_MS,
    })),
  });
  // The list the server step read, reused while it is fresh.
  const mcpServers =
    useListMcpServersForOrg(undefined, undefined, {
      throwOnError: false,
      staleTime: PLUGIN_STALE_TIME_MS,
    }).data?.mcpServers ?? [];
  const pending = plugins.isPending || details.some((query) => query.isPending);
  const failed = plugins.isError || details.some((query) => query.isError);
  const delivered = pluginsForEveryAgent(
    details.flatMap((query) => (query.data === undefined ? [] : [query.data])),
  );
  const unreachable = unreachablePluginServers(delivered, servers, mcpServers);
  const gateways = pluginGateways(delivered);
  const withoutAuthorization = pluginServersWithoutAuthorization(
    delivered,
    mcpServers,
  );
  const agent = agentName || "the agent";
  const them = unreachable.length === 1 ? "it" : "them";

  return (
    <div className="space-y-4 text-sm">
      <p>
        The job gets {agent}&apos;s plugins: their MCP servers, skills and
        hooks. Speakeasy also adds its observability plugin, which sends the
        job&apos;s hook events.
      </p>
      <p className="text-muted-foreground">
        Below are the plugins installed for <strong>Everyone</strong> in this
        project. The list is partial: the job also gets plugins assigned to
        roles {agent} holds, and those from other projects.
      </p>
      <DeliveredPlugins
        pending={pending}
        failed={failed}
        delivered={delivered}
        agent={agent}
      />
      {gateways.length > 0 && (
        <p className="text-muted-foreground">
          The job skips {gateways.join(", ")}: gateways do not accept agents
          yet.
        </p>
      )}
      {withoutAuthorization.length > 0 && (
        <p className="text-muted-foreground">
          The job skips {withoutAuthorization.join(", ")}: a job gets its token
          from a Speakeasy authorization server, and{" "}
          {withoutAuthorization.length === 1 ? "that server has" : "they have"}{" "}
          none.
        </p>
      )}
      {unreachable.length > 0 && (
        <SetupWarning>
          {agentName || "The agent"} cannot use {unreachable.join(", ")}, so the
          job&apos;s tools will show {them} as needing authentication. Go back
          to MCP Selection to add {them}.
        </SetupWarning>
      )}
      <p className="text-muted-foreground">
        Servers that need each person to sign in do not work in a job: there is
        nobody to sign in.
      </p>
    </div>
  );
}

/**
 * The plugins assigned to Everyone, or why there are none to show. Reading is
 * shown in the list's own shape, so the step does not jump once it loads.
 */
function DeliveredPlugins({
  pending,
  failed,
  delivered,
  agent,
}: {
  pending: boolean;
  failed: boolean;
  delivered: Plugin[];
  agent: string;
}): JSX.Element {
  const routes = useRoutes();
  const pluginsPage = (
    <Link className="underline" to={routes.plugins.href()}>
      Plugins page
    </Link>
  );
  if (pending) {
    return (
      <div
        role="status"
        className="border-border space-y-2 border px-3 py-2.5"
        aria-label="Reading this project's plugins"
      >
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-56" />
      </div>
    );
  }
  if (failed) {
    return (
      <SetupWarning>
        This project&apos;s plugins could not be read. Check them on the{" "}
        {pluginsPage}.
      </SetupWarning>
    );
  }
  if (delivered.length === 0) {
    return (
      <SetupWarning>
        No plugin in this project is installed for Everyone, so unless {agent}{" "}
        holds a role with plugins, the job gets no MCP servers. A plugin that is
        only Available to Everyone is not installed on a job. On the{" "}
        {pluginsPage}, assign a plugin to <strong>Everyone</strong> as Required
        or On by default, or to a role and give the agent that role.
      </SetupWarning>
    );
  }
  return (
    <ul className="border-border divide-border divide-y border">
      {delivered.map((plugin) => {
        const servers = (plugin.servers ?? []).map(
          (server) => server.displayName,
        );
        return (
          <li key={plugin.id} className="px-3 py-2.5">
            <p className="font-medium">{plugin.name}</p>
            <p className="text-muted-foreground">
              {servers.length === 0 ? "No MCP servers" : servers.join(", ")}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

/** Which of the repository's jobs the rule admits, as the catalog labels them. */
export function JobScopeChoice({
  choices,
  value,
  onChange,
}: {
  choices: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <Label id="github-actions-scope">Which jobs</Label>
      <RadioGroup
        aria-labelledby="github-actions-scope"
        value={value}
        onValueChange={onChange}
        className="gap-1.5"
      >
        {choices.map((choice) => {
          const id = `github-actions-scope-${choice.key}`;
          return (
            <div key={choice.key} className="flex items-center gap-2">
              <RadioGroupItem value={choice.key} id={id} />
              <Label htmlFor={id} className="cursor-pointer font-normal">
                {choice.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      <Text small muted>
        The whole repository admits every branch and pull request. An
        environment is the way to require GitHub&apos;s protection rules, such
        as required reviewers, before a job signs in.
      </Text>
    </div>
  );
}

/**
 * Why forks cannot use a workflow that runs on pull requests, and the event
 * that would let them.
 */
const FORK_NOTE = (
  <>
    On <code>pull_request</code>, forks get no GitHub token, and the job&apos;s{" "}
    <code>if:</code> skips them. Don&apos;t run it on{" "}
    <code>pull_request_target</code>: that event gives a fork&apos;s pull
    request a token the rule admits.
  </>
);

/** What the workflow's triggers and the rule mean together, by scope. */
function ScopeNote({ scope }: { scope: JobScope }): JSX.Element {
  switch (scope.kind) {
    case "repository":
      return <li>{FORK_NOTE}</li>;
    case "branch":
      return (
        <li>
          The rule admits jobs on <code>{scope.name}</code> that use no
          environment, so the workflow runs on pushes to it. A pull request job
          is refused.
        </li>
      );
    case "environment":
      return (
        <li>
          The rule admits only jobs that use the <code>{scope.name}</code>{" "}
          environment. Set its protection rules under Settings &gt; Environments
          before you add the workflow: GitHub creates an environment a workflow
          names with no rules.
        </li>
      );
    case "pull_request":
      return (
        <li>
          The rule admits only pull request jobs that use no environment, so the
          workflow runs on pull requests alone. {FORK_NOTE}
        </li>
      );
  }
}

export function WorkflowStep({ scope }: { scope: JobScope }): JSX.Element {
  const organization = useOrganization();
  const project = useProject();
  const [agent, setAgent] = useState<WorkflowAgent>("claude");
  const selected = WORKFLOW_AGENTS[agent];
  const workflow = workflowYaml({
    agent,
    organizationId: organization.id,
    projectSlug: project.slug,
    controlPlaneUrl: getServerURL(),
    scope,
  });
  return (
    <div className="space-y-4 text-sm">
      <p>
        Add this workflow to the repository, or add the steps to a job you
        already have. The job proves who it is with its own GitHub token, so no
        Speakeasy secret is stored in GitHub.
      </p>
      <PanelTabs
        aria-label="Workflow agent"
        value={agent}
        onValueChange={setAgent}
        tabs={WORKFLOW_AGENT_OPTIONS.map((option) => ({
          value: option.id,
          label: (
            <span className="inline-flex items-center gap-2">
              <AgentProviderIcon
                source={option.iconSource}
                className="size-3.5"
              />
              {option.name}
            </span>
          ),
        }))}
      >
        {/* One panel, for the chosen agent: the other tab's workflow would
            be a second copy of the same file under the same name. */}
        <PanelTab value={agent}>
          <p>
            Add <code>{selected.secret}</code> to your repository&apos;s Actions
            secrets for {selected.name}, then replace the example prompt with
            your task. Speakeasy access uses the job&apos;s GitHub token
            separately.
          </p>
          {workflow.yaml === null ? (
            <SetupError>{workflow.problem}</SetupError>
          ) : (
            <CodeBlock
              value={workflow.yaml}
              label=".github/workflows/speakeasy.yml"
              language="yaml"
            />
          )}
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <code>id-token: write</code> is required. Without it the job has
              no GitHub token.
            </li>
            <li>
              <code>project</code> is where hook events go. The agent may send
              them to <strong>{project.name || project.slug}</strong> only;
              leave it out to send none.
            </li>
            <ScopeNote scope={scope} />
            <li>
              Keep <strong>Connect to Speakeasy</strong> before{" "}
              <strong>Run {selected.name}</strong> in the same job so it can use
              the configured MCP servers.
            </li>
            <li>
              The device agent action&apos;s <code>@{ACTION_TAG}</code> tag
              moves to each {ACTION_TAG}.x release. If your team requires commit
              pins, pin a release commit instead.
            </li>
          </ul>
          <Links
            links={[
              { href: ACTION_DOCS, label: "Device agent action" },
              { href: selected.docs, label: `${selected.name} action` },
            ]}
          />
        </PanelTab>
      </PanelTabs>
    </div>
  );
}

/**
 * Who can sign in as the agent under the rule about to be saved. Every
 * choice is open to someone who can push to the repository; only an
 * environment's protection rules can put a review in front of it.
 */
export function RepositoryRuleWarning({
  scope,
  repository,
  agent,
  subject,
}: {
  scope: JobScope;
  repository: string;
  agent: string;
  subject: string;
}): JSX.Element {
  const [who, risk] = ruleWarningText(scope, repository, agent, subject);
  return (
    <Alert variant={scope.kind === "environment" ? "info" : "warning"}>
      <div className="space-y-2 text-sm">
        <p>{who}</p>
        <p>{risk}</p>
      </div>
    </Alert>
  );
}

/** Who the rule admits, and the risk the reader should weigh, by scope. */
function ruleWarningText(
  scope: JobScope,
  repository: string,
  agent: string,
  subject: string,
): [ReactNode, ReactNode] {
  const rule = (
    <>
      The rule is <code>{subject}</code>.
    </>
  );
  switch (scope.kind) {
    case "repository":
      return [
        <>
          Every job in <strong>{repository}</strong> can sign in as {agent}:
          every branch, tag, environment and pull request. {rule}
        </>,
        <>
          Anyone who can push a branch to the repository can run such a job and
          use {agent}&apos;s access. On the <code>pull_request</code> event,
          pull requests from forks get no GitHub token, so outside contributors
          cannot. A <code>pull_request_target</code> workflow with{" "}
          <code>id-token: write</code> is admitted too, and if it checks out the
          pull request&apos;s head, code from a fork runs as {agent}. To admit
          fewer jobs, choose pull requests, one branch or one environment.
        </>,
      ];
    case "branch":
      return [
        <>
          Jobs on <strong>{scope.name}</strong> in {repository} can sign in as{" "}
          {agent}, unless they use an environment. Pull request jobs cannot.{" "}
          {rule}
        </>,
        <>
          Anyone who can push to {scope.name} can run such a job, so protect the
          branch if only reviewed code should use {agent}&apos;s access.
        </>,
      ];
    case "environment":
      return [
        <>
          Only jobs in {repository} that use the <strong>{scope.name}</strong>{" "}
          environment can sign in as {agent}, and GitHub starts them only once
          the environment&apos;s protection rules pass. {rule}
        </>,
        <>
          Set those rules, such as required reviewers or the branches that may
          deploy, under the repository&apos;s Settings &gt; Environments before
          you add the workflow. A workflow that names an environment nobody has
          set up creates it with no rules.
        </>,
      ];
    case "pull_request":
      return [
        <>
          Pull request jobs in <strong>{repository}</strong> can sign in as{" "}
          {agent}, unless they use an environment. Jobs on pushed branches and
          tags cannot. {rule}
        </>,
        <>
          Anyone who can push a branch can run such a job with the workflow as
          their branch has it, so nobody reviews it first. Forks get no token on{" "}
          <code>pull_request</code>, but a <code>pull_request_target</code>{" "}
          workflow is admitted too, a fork&apos;s pull request included.
        </>,
      ];
  }
}
